import React from "react";
import { Routes, Route } from "react-router-dom";

// Component imports
import HomePage from "../home/HomePage";
import RiskPage from "../risks/RiskPage";
import PageNotFound from "../common/PageNotFound";
import EnvironmentNotFound from "../common/EnvironmentNotFound";
import OneConferenceFreeTrial from "../common/OneConferenceFreeTrial";
import TrialExpired from "../common/TrialExpired";
import CreateTrialEnvironment from "../common/CreateTrialEnvironment";
import ViewRiskPage from "../risks/ViewRiskPage";
import MeasuresPage from "../measures/MeasuresPage";
import ApplicabilityPage from "../applicability/ApplicabilityPage";
import LoginPage from "../login/LoginPage";
import SetPasswordPage from "../login/SetPasswordPage";
import ResetPasswordPage from "../login/ResetPasswordPage";
import LoginCallbackPage from "../login/LoginCallbackPage";
import PaymentPage from "../payment/PaymentPage";
import PaymentSuccess from "../payment/PaymentSuccess";
import PaymentCancelled from "../payment/PaymentCancelled";
import SubscriptionsPage from "../payment/SubscriptionsPage";

// Import page components
import { RiskTemplatePage, AddRiskTemplatePage, EditRiskTemplatePage } from "../risktemplates";
import { EnvironmentPage, AddEnvironmentPage, ViewEnvironmentPage, ManageEnvironmentPage } from "../environments";
import { RequestPage } from "../requests";
import { OrganizationPage, AddOrganizationPage, EditOrganizationPage } from "../organizations";
import { ProjectPage, AddProjectPage, EditProjectPage } from "../projects";
import { UserPage, AddUserPage, EditUserPage } from "../users";
import { CoPilotPage, CoPilotManagementPage, AddCoPilotPage, EditCoPilotPage } from "../copilot";
import { PrepareRiskEvaluationPage } from "../riskevaluation";
import { ActivityPage } from "../activities";
import { MailingPage, UnsubscribePage } from "../mailing";
import RiskReviewPage from "../risks/RiskReviewPage";
import HelpPage from "../help/HelpPage";

// Route components for different user states
export const LoggedInRoutes = ({ currentUser, refreshData, setRefreshData }) => (
  <Routes>
    {/* Payment routes - always accessible when logged in */}
    <Route path="/payment" element={<PaymentPage />} />
    <Route path="/payment-success" element={<PaymentSuccess />} />
    <Route path="/payment-cancelled" element={<PaymentCancelled />} />
    
    <Route path="/" element={<HomePage />} />
    <Route path="risks" element={<RiskPage refreshData={refreshData} setRefreshData={setRefreshData}/>} />
    <Route path="risk-review" element={<RiskReviewPage refreshData={refreshData} setRefreshData={setRefreshData}/>} />
    <Route path="viewrisk" element={<ViewRiskPage refreshData={refreshData} />} />
    <Route path="measures" element={<MeasuresPage />} />
    
    {/* Routes for superadmin in main environment */}
    {(currentUser.role === "superadmin" && currentUser.environmentId === 'main') && (
      <>
        <Route path="copilotmanagement" element={<CoPilotManagementPage />} />
        <Route path="addcopilot" element={<AddCoPilotPage />} />
        <Route path="editcopilot" element={<EditCoPilotPage />} />
        <Route path="environments" element={<EnvironmentPage />} />
        <Route path="addenvironment" element={<AddEnvironmentPage />} />
        <Route path="viewenvironment" element={<ViewEnvironmentPage />} />
        <Route path="manageenvironment" element={<ManageEnvironmentPage />} />
        <Route path="requests" element={<RequestPage />} />
        <Route path="activities" element={<ActivityPage />} />
        <Route path="mailing" element={<MailingPage />} />
      </>
    )}

    {/* Routes for superadmin in knmt environment */}
    {(currentUser.role === "superadmin" && currentUser.environmentId === 'knmt') && (
      <>
        <Route path="environments" element={<EnvironmentPage />} />
        <Route path="activities" element={<ActivityPage />} />
      </>
    )} 
    
    {/* Routes for superadmin */}
    {currentUser.role === "superadmin" && (
      <>
        <Route path="organizations" element={<OrganizationPage />} />
        <Route path="addorg" element={<AddOrganizationPage />} />
        <Route path="editorg" element={<EditOrganizationPage />} />
        <Route path="risktemplates" element={<RiskTemplatePage />} />
        <Route path="addrisktemplate" element={<AddRiskTemplatePage />} />
        <Route path="editrisktemplate" element={<EditRiskTemplatePage />} />
      </>
    )}
    
    {/* Routes for superadmin or admin */}
    {(currentUser.role === "superadmin" || currentUser.role === "admin") && (
      <>
        <Route path="applicability" element={<ApplicabilityPage />} />
        <Route path="copilot" element={<CoPilotPage />} />
        <Route path="prepare-evaluation" element={<PrepareRiskEvaluationPage refreshData={refreshData} />} />
        <Route path="projects" element={<ProjectPage />} />
        <Route path="addproject" element={<AddProjectPage />} />
        <Route path="editproject" element={<EditProjectPage />} />
        <Route path="users" element={<UserPage />} />
        <Route path="adduser" element={<AddUserPage />} />
        <Route path="subscriptions" element={<SubscriptionsPage />} />
      </>
    )}
    
    <Route path="edituser" element={<EditUserPage />} />
    <Route path="help" element={<HelpPage />} />
    <Route path="/set-password" element={<SetPasswordPage />} />
    <Route path="/unsubscribe" element={<UnsubscribePage />} />
    <Route path="/create-trial-environment" element={<CreateTrialEnvironment />} />
    <Route path="/oneconference-freetrial" element={<OneConferenceFreeTrial />} />
    <Route path="*" element={<PageNotFound />} />
  </Routes>
);

export const LoggedOutRoutes = () => (
  <Routes>
    <Route path="/set-password" element={<SetPasswordPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/unsubscribe" element={<UnsubscribePage />} />
    <Route path="/login-callback" element={<LoginCallbackPage />} />
    <Route path="/create-trial-environment" element={<CreateTrialEnvironment />} />
    <Route path="/oneconference-freetrial" element={<OneConferenceFreeTrial />} />
    <Route path="/environment-not-found" element={<EnvironmentNotFound />} />
    <Route path="/payment-success" element={<PaymentSuccess />} />
    <Route path="/payment-cancelled" element={<PaymentCancelled />} />
    <Route path="*" element={<LoginPage />} />
  </Routes>
);

// Helper function to determine if particles should be displayed
export const shouldShowParticles = () => window.innerWidth > 450;